import type React from "react";
import {useEffect, useState} from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import {faTrophy} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {useLevelV2} from "~/api/hooks/useLevelV2";
import {usePersonalBestV2} from "~/api/hooks/usePersonalBestV2";
import {auth} from "~/firebase/firebase";

interface MedalDisplayProps {
  /**
   * Level ID to load relevant data.
   */
  levelId?: string;
}

/**
 * Computes the medal set based on the relative ratio.
 * If ratio <= 1.1, the user earns all medals:
 *   best-to-worst order: ["Master", "Gold", "Silver", "Bronze"]
 * If ratio <= 1.4, then: ["Gold", "Silver", "Bronze"]
 * If ratio <= 2, then: ["Silver", "Bronze"]
 * Otherwise, only: ["Bronze"]
 *
 * The result is reversed so that the worst medal appears first on the left.
 */
const computeMedals = (ratio: number): string[] => {
  if (ratio <= 1.1) return ["Master", "Gold", "Silver", "Bronze"];
  if (ratio <= 1.4) return ["Gold", "Silver", "Bronze"];
  if (ratio <= 2) return ["Silver", "Bronze"];
  return ["Bronze"];
};

// Define Tailwind gradient classes for each medal.
const medalGradientClasses: Record<string, string> = {
  Bronze: "bg-gradient-to-br from-orange-600 via-orange-500 to-black",
  Silver: "bg-gradient-to-br from-gray-500 via-gray-400 to-black",
  Gold: "bg-gradient-to-br from-yellow-500 via-yellow-400 to-black",
  Master: "bg-gradient-to-br from-purple-600 via-purple-500 to-black",
};

const MedalDisplay: React.FC<MedalDisplayProps> = ({levelId}) => {
  const [myUser] = useAuthState(auth);
  const {data: level} = useLevelV2({id: levelId});
  const {data: personalBestV2} = usePersonalBestV2({
    levelId,
    id: myUser?.uid,
  });
  const [medals, setMedals] = useState<string[]>([]);

  useEffect(() => {
    if (level && personalBestV2?.replay) {
      const pbSteps = personalBestV2.replay.totalSteps;
      if (level.worldRecordSteps) {
        const computedMedals = computeMedals(
          pbSteps / level.worldRecordSteps,
        ).reverse();
        setMedals(computedMedals);
      } else {
        setMedals([]);
      }
    } else {
      setMedals([]);
    }
  }, [level, personalBestV2]);

  return (
    <div className="flex justify-center my-2 min-h-[2.5rem]">
      {medals.length > 0 ? (
        <div className="flex items-center">
          {medals.map((medalName, index) => (
            <div
              key={medalName}
              style={{
                marginLeft: index === 0 ? 0 : "-6px",
                zIndex: index + 1,
              }}
              className={`relative ${medalGradientClasses[medalName]} w-10 h-10 border-2 border-white rounded-full p-1 flex items-center justify-center shadow-lg transform transition-all duration-300 hover:scale-110 hover:shadow-2xl`}
            >
              <FontAwesomeIcon
                icon={faTrophy}
                size="lg"
                className="text-white"
              />
            </div>
          ))}
        </div>
      ) : (
        <span className="text-gray-500 italic text-xs">No medals yet</span>
      )}
    </div>
  );
};

export default MedalDisplay;
